import { useCallback, useMemo } from "react"
import { Plus } from "lucide-react"
import type { BoardId, KanbanBoard, KanbanRenderBoardProps } from "@/components/KanbanEngine"
import { KanbanEngine } from "@/components/KanbanEngine"
import { Card, CardHeader, CardPanel, CardTitle } from "@/components/ui/card"
import { cn } from "@/utils/Functions"
import { AriDealKanbanCard } from "./AriDealKanbanCard"
import { DealKanbanCard } from "./DealKanbanCard"
import type { Deal, PipelineStage } from "@/types/deal.types"
import { ARI_PIPELINE_COLUMNS, PIPELINE_COLUMNS } from "@/constants/deal.constants"
import { formatCurrency } from "@/views/deals/utils"

interface DealPipelineBoardProps {
  deals: Deal[]
  onDealsChange: (deals: Deal[]) => void
  visibleColumns: PipelineStage[]
  isAriPipeline?: boolean
  /**
   * Forwarded to each DealKanbanCard so the card's "Delete" menu item can
   * signal intent. The page-level AlertDialog (DealPipelinePage) owns the
   * confirmation + the deleteDeal mutation — the board just plumbs the
   * handler through. Optional so the Ari pipeline variant (which renders a
   * different card without delete) stays unaffected.
   */
  onRequestDelete?: (deal: Deal) => void
}

const EMPTY_DEAL_IDS_BY_STAGE: Partial<Record<PipelineStage, string[]>> = {
  lead: [],
  in_progress: [],
  signing: [],
  delivery: [],
  complete: [],
  won: [],
  lost: [],
  closed: [],
}

export function DealPipelineBoard({ deals, onDealsChange, visibleColumns, isAriPipeline, onRequestDelete }: DealPipelineBoardProps) {
  const visibleColumnSet = useMemo(() => new Set<PipelineStage>(visibleColumns), [visibleColumns])

  const allColumns = isAriPipeline ? ARI_PIPELINE_COLUMNS : PIPELINE_COLUMNS

  const columns = useMemo(
    () => allColumns.filter((column) => visibleColumnSet.has(column.id)),
    [allColumns, visibleColumnSet],
  )

  const cardsById = useMemo(() => {
    const nextCardsById: Record<string, Deal> = {}
    for (const deal of deals) {
      nextCardsById[deal.id] = deal
    }
    return nextCardsById
  }, [deals])

  const boardsById = useMemo(() => {
    const dealIdsByStage = { ...EMPTY_DEAL_IDS_BY_STAGE } as Record<PipelineStage, string[]>
    for (const column of columns) {
      dealIdsByStage[column.id] = []
    }

    for (const deal of deals) {
      if (!visibleColumnSet.has(deal.stage)) {
        continue
      }
      dealIdsByStage[deal.stage].push(deal.id)
    }

    const nextBoardsById: Record<BoardId, KanbanBoard> = {}
    for (const column of columns) {
      nextBoardsById[column.id] = {
        id: column.id,
        title: column.title,
        cardIds: dealIdsByStage[column.id],
      }
    }

    return nextBoardsById
  }, [columns, deals, visibleColumnSet])

  const boardOrder = useMemo(() => columns.map((column) => column.id as BoardId), [columns])

  const columnById = useMemo(() => {
    const nextColumnById = new Map<PipelineStage, (typeof PIPELINE_COLUMNS)[number]>()
    for (const column of columns) {
      nextColumnById.set(column.id, column)
    }
    return nextColumnById
  }, [columns])

  const handleBoardsChange = useCallback(
    (nextBoardsById: Record<BoardId, KanbanBoard>) => {
      const nextStageByDealId = new Map<string, PipelineStage>()

      for (const [boardId, board] of Object.entries(nextBoardsById)) {
        if (!visibleColumnSet.has(boardId as PipelineStage)) {
          continue
        }

        for (const cardId of board.cardIds) {
          nextStageByDealId.set(cardId, boardId as PipelineStage)
        }
      }

      let hasStageChange = false
      const updatedDeals = deals.map((deal) => {
        const nextStage = nextStageByDealId.get(deal.id)
        if (!nextStage || nextStage === deal.stage) {
          return deal
        }

        hasStageChange = true
        return { ...deal, stage: nextStage }
      })

      if (hasStageChange) {
        onDealsChange(updatedDeals)
      }
    },
    [deals, onDealsChange, visibleColumnSet],
  )

  const handleMoveToStage = useCallback(
    (dealId: string, targetStage: PipelineStage) => {
      const updatedDeals = deals.map((deal) =>
        deal.id === dealId ? { ...deal, stage: targetStage } : deal,
      )
      onDealsChange(updatedDeals)
    },
    [deals, onDealsChange],
  )

  const renderBoard = useCallback(
    ({ board, cards, isOver, body }: KanbanRenderBoardProps<Deal>) => {
      const stage = board.id as PipelineStage
      const column = columnById.get(stage)
      const totalValue = cards.reduce((sum, deal) => sum + deal.value, 0)

      if (isAriPipeline && column) {
        return (
          <div
            key={board.id}
            className="flex h-full w-[230px] shrink-0 flex-col overflow-hidden rounded-[10px] border border-border bg-muted/20"
          >
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5">
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-bold",
                  column.color,
                  column.pillTextClass ?? "text-white",
                )}
              >
                {column.title}
              </span>
              <span className="text-[11px] font-semibold text-muted-foreground">{cards.length}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{formatCurrency(totalValue)}</span>
              <button
                type="button"
                aria-label={`Add deal to ${column.title}`}
                className="flex size-5 shrink-0 items-center justify-center rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground"
              >
                <Plus className="size-3" aria-hidden="true" />
              </button>
            </div>
            <div className={cn("min-h-0 flex-1 overflow-y-auto p-2", isOver && "bg-primary/5")}>{body}</div>
          </div>
        )
      }

      return (
        <Card key={board.id} className="flex h-full w-80 shrink-0 gap-0">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  aria-label={`${board.title} stage indicator`}
                  className={cn("size-2 shrink-0 rounded-full", column?.color ?? "bg-muted-foreground/40")}
                />
                <CardTitle className="truncate text-sm font-semibold">{board.title}</CardTitle>
                <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {cards.length}
                </span>
              </div>

              <span className="shrink-0 text-xs font-medium text-muted-foreground">{formatCurrency(totalValue)}</span>
            </div>
          </CardHeader>

          <CardPanel className="p-1.5">
            <div className={cn(isOver && "border-primary/20 bg-primary/5")}>{body}</div>
          </CardPanel>
        </Card>
      )
    },
    [columnById, isAriPipeline],
  )

  return (
    <div className={cn("h-full w-full overflow-x-auto", isAriPipeline ? "p-3" : "px-4 pt-4 pb-4")}>
      <KanbanEngine.Root
        cardsById={cardsById}
        initialBoardsById={boardsById}
        boardOrder={boardOrder}
        onBoardsChange={handleBoardsChange}
        className="h-full"
      >
        <KanbanEngine.Columns<Deal>
          wrapSortableItem={false}
          className={cn(
            "flex after:w-1 after:shrink-0 after:content-['']",
            isAriPipeline ? "gap-2.5" : "gap-4",
          )}
          boardBodyClassName="min-h-40 gap-2 border-transparent bg-transparent p-0"
          emptyState={
            <div
              className={cn(
                "flex items-center justify-center py-5 text-xs italic text-muted-foreground/60",
                !isAriPipeline && "h-24 rounded-lg border-2 border-dashed border-border/50",
              )}
            >
              No deals
            </div>
          }
          renderBoard={renderBoard}
          renderCard={({ card, isDragging }) =>
            isAriPipeline ? (
              <AriDealKanbanCard
                deal={card}
                stage={card.stage}
                isDragging={isDragging}
                onMoveToStage={handleMoveToStage}
              />
            ) : (
              <DealKanbanCard deal={card} isDragging={isDragging} onRequestDelete={onRequestDelete} />
            )
          }
        />
      </KanbanEngine.Root>
    </div>
  )
}
